import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOfferTags } from 'actions/OfferActions'
import useQueryParams from 'hooks/useQueryParams'
import { buildOfferTagsCacheKey } from 'lib/experiences/experienceUtils'

interface UseOfferTagsOptions {
  tagType?: App.OfferTagType
  includeAssignments?: boolean
  includedAssignmentEntityTypeIn?: Array<App.OfferTagAssignment['entityType']>
}

interface UseOfferTagsResult {
  tags: Array<App.OfferTag>
  fetching: boolean
}

const EMPTY_OFFER_TAGS: Array<App.OfferTag> = []

function useOfferTags(
  offerId: string | undefined,
  options: UseOfferTagsOptions = {},
): UseOfferTagsResult {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const { tagType, includeAssignments, includedAssignmentEntityTypeIn } = options

  const checkInDate = queryParams.get('checkIn') ?? undefined
  const checkOutDate = queryParams.get('checkOut') ?? undefined

  const cacheKey = useMemo(() => {
    if (!offerId) return undefined
    return buildOfferTagsCacheKey(
      offerId,
      checkInDate,
      checkOutDate,
      tagType,
      includeAssignments,
      includedAssignmentEntityTypeIn,
    )
  }, [offerId, checkInDate, checkOutDate, tagType, includeAssignments, includedAssignmentEntityTypeIn])

  const tagData = useAppSelector(state => {
    if (!cacheKey) return undefined
    return state.offer.offerTags?.[cacheKey]
  })

  const tags = tagData?.tags ?? EMPTY_OFFER_TAGS

  useEffect(() => {
    if (!offerId) {
      return
    }

    dispatch(fetchOfferTags(offerId, {
      travelStartDate: checkInDate,
      travelEndDate: checkOutDate,
      travelDateOverlap: true,
      tagType,
      includeAssignments,
      includedAssignmentEntityTypeIn,
    }))
  }, [dispatch, offerId, checkInDate, checkOutDate, tagType, includeAssignments, includedAssignmentEntityTypeIn])

  return {
    tags,
    fetching: offerId ? (tagData?.fetching ?? true) : false,
  }
}

export default useOfferTags
